import React, {useEffect, useRef, useState} from 'react';
import { useSelector, useDispatch } from "react-redux";

import { useTranslation } from 'react-i18next';

import { Gantt } from 'dhtmlx-gantt';
// import 'dhtmlx-gantt/codebase/dhtmlxgantt.css';
// import 'dhtmlx-gantt/codebase/skins/dhtmlxgantt_material.css';
import '../../../../styles/gantt/skins/dhtmlxgantt_material.css';
import '../../../../styles/gantt/controls_styles.css';
import '../../../../styles/gantt/overrides.css';

import { getFieldFormatters } from "../../../../utilities/DataSourceUtilities";

import GanttToolbar from "./GanttToolbar";
import useDataSource from "../../../../hooks/useDataSource";
import {areLoadDependenciesMet} from "../../../../utilities/DataSourceUtilities";

import {
    formatDate,
    formatNumber,
    formatStyle,
    formatText,
    formatSuffix,
    formatAvatar,
    FormatLink,
    FormatModalLink,
    formatGoodBad
} from "../../../../utilities/FormatUtilities";

import {compareValues, sortDates} from "../../../../utilities/DataUtilities";

import {appSetGanttUserSelections, appSetModalContent} from "../../../../redux/app/appActions";
import {persistGanttAddExpandedTask, persistGanttRemoveExpandedTask} from "../../../../redux/persist/persistActions";
import {dataAddFilter, dataRemoveFilter, filterUpdateConditionAction} from "../../../../redux/data/dataActions";
import ClearFilterBtn from "../../../application/ClearFilterBtn";
import SettingsForm from "../settings/SettingsForm";
import {findComponent} from "../../../../redux/page/pageActions";

import { styled, LinearProgress, Card, CardHeader, CardContent } from '@mui/material';

const PREFIX = 'Gantt';

const classes = {
    root: `${PREFIX}-root`,
    menuButton: `${PREFIX}-menuButton`,
    linearProgress: `${PREFIX}-linearProgress`,
    card: `${PREFIX}-card`,
    cardHeader: `${PREFIX}-cardHeader`,
    rightToolbar: `${PREFIX}-rightToolbar`,
    leftToolbar: `${PREFIX}-leftToolbar`,
    btnGroup: `${PREFIX}-btnGroup`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        width: '100%',
        height: 'calc(100% - 1px)',
        margin: '0px',
        padding: '0px',
		backgroundColor: theme.palette.background.default
    },

    [`& .${classes.menuButton}`]: {
        marginRight: theme.spacing(2),
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.card}`]: {
        height: '100%',
        // padding: '8px',
        boxShadow: 'none',
		backgroundColor: theme.palette.background.default
        // backgroundColor: 'rgba(0,0,0,0)'
    },

    [`& .${classes.cardHeader}`]: {
        //height: '68px',
        padding: '8px 8px 0px 8px',
        alignItems: 'start',
        textAlign: 'left',
        textTransform: 'none',
    },

    [`& .${classes.rightToolbar}`]: {
        marginLeft: "auto",
        marginRight: -12
    },

    [`& .${classes.leftToolbar}`]: {
        // marginLeft: "auto",
        // marginRight: -12
    },

    [`&.${classes.btnGroup}`]: {
        position: "relative",
        display: "inline-block",
        verticalAlign: "middle",
        padding: "5px 0 5px 5px"
    }
}));

export default function GanttChart({id, configuration, xId}) {
	const dispatch = useDispatch();
    const { t } = useTranslation();

    // App State
    const isThemeLight = useSelector(state => state.persist.themeLight);
    const filters = useSelector(state => state.data.filters);
    const modalContent = useSelector(state => state.app.modalContent);
    const dataDate = useSelector(state => state.data.dataDate);
    const expandedGanttTaskIDs = useSelector(state => state.persist.expandedGanttTaskIDs);
    const ragStyle = useSelector(state => state.config.data.ragStyle);
    const userSelections = useSelector(state => state.app.ganttUserSelections);
	const pageID = useSelector(state => state.app.pageID);
	const editXId = useSelector(state => state.page.componentEdit.xId);
	const componentUpdated = useSelector(state => state.page.componentUpdated);
	const componentWrapperEdit = useSelector(state => state.page.componentWrapperEdit);
	const pages = useSelector(state => state.page.pages);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	const activeRole = useSelector(state => state.persist.activeRoleName);

    // Component State
	const [compConfig, setCompConfig] = useState(configuration);
	const [title, setTitle] = useState(compConfig.name?.title ? compConfig.name.title : '');
	const [subtitle, setSubTitle] = useState(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
    const [myGantt, setMyGantt] = useState(undefined);
    const [cachedSettings, setCachedSettings] = useState({});
    const [zoomedToFit, setZoomedToFit] = useState(false);
	const [cardHeaderStyle, setCardHeaderStyle] = useState({});
	const [cardContentStyle, setCardContentStyle] = useState({});

    // Baselines are enabled if present in data... then the toolbar.commands config
    // sets up the state
    const [baselineEnabled, setBaselineEnabled] = useState(false);
    const [baselineVisible, setBaselineVisible] = useState(true);
    const [milestonesVisible, setMilestonesVisible] = useState(true);
    const [splitParentsVisible, setSplitParentsVisible] = useState(false);
    const [progressVisible, setProgressVisible] = useState(true);
    const [zoomTodayDisabled, setZoomTodayDisabled] = useState(true);
    const [performanceKey, setPerformanceKey] = useState(null);
    const [milestoneKey, setMilestoneKey] = useState(null);
    const [showMilestoneKey, setShowMilestoneKey] = useState(false);

    // Controls the display i.e. if false then only the table
    // will be visible
    const [tasksVisible, setTasksVisible] = useState(true);

    // const [gantt, setGantt] = useState(null);
    let toolbarStyle = {
        backgroundColor: isThemeLight ? "#F2F4F5" : "rgba(125, 125, 125, 0.2)"
    };

	const dataSourceName = compConfig.dataSource?.name !== undefined ? compConfig.dataSource.name : '';
	const rollupConfig = compConfig.dataSource?.rollup !== undefined ? compConfig.dataSource.rollup : {};
	const leftColumns = compConfig.dataSource?.leftColumns !== undefined ? compConfig.dataSource.leftColumns : [];
	const fields = compConfig.dataSource?.fields !== undefined ? compConfig.dataSource.fields : {};
	const dependencies = compConfig.dataSource?.dependencies !== undefined ? compConfig.dataSource.dependencies : null;
	const displayDataDate = compConfig.element?.displayDataDate !== undefined ? compConfig.element.displayDataDate : true;
	const gridWidth = compConfig.element?.gridWidth !== undefined ? compConfig.element.gridWidth : null;
	const initialZoomLevel = compConfig.element?.initialZoomLevel !== undefined ? compConfig.element.initialZoomLevel : 'year';
	const toolbar = compConfig.element?.toolbar !== undefined ? compConfig.element.toolbar : {};
	const expandNodes = compConfig.element?.expandNodes !== undefined ? compConfig.element.expandNodes : false;
	const styles = compConfig?.element?.styles !== undefined ? compConfig.element.styles : {};

    // Set Fields
    const idField = fields.idField;
    const startDateField = fields.startDateField;
    const endDateField = fields.endDateField;
    const plannedStartDateField = fields.plannedStartDateField;
    const plannedEndDateField = fields.plannedEndDateField;
    const progressField = fields.progressField;
    const statusField = fields.statusField;
    const nameField = fields.nameField;
    const nodeTypeField = fields.nodeTypeField ? fields.nodeTypeField : null;
    const performanceStyleType = fields.performanceType ? fields.performanceType : null;
    const sortType = fields.sortType ? fields.sortType : null;
    const msPerfField = fields.milestonePerformanceField !== undefined ? fields.milestonePerformanceField : null;

	const ganttRef = useRef(null);
	const dataSource = useDataSource(dataSourceName);

    useEffect(() => {
        // console.log('Gantt:useEffect(() => {}, []):Start');

        // Baselines enabled is determined both on the data and on the state of the config
        if (plannedStartDateField && plannedEndDateField) {
            setBaselineEnabled(true);
        } else {
            setBaselineEnabled(false);
        }

        // Set the Performance Key
        if (performanceStyleType) {
            if (performanceStyleType.type.toLowerCase() === 'variance'
                || performanceStyleType.type.toLowerCase() === 'start') {
                const performanceStyleKey = (
                    <React.Fragment>
                        <div className={classes.btnGroup} style={{position: "relative", display: "inline-block", verticalAlign: "middle", padding: "5px 0 5px 5px"}}>
                            <div style={{float: 'left', paddingTop: '6px'}}>
                                <i className="fa fa-square" style={{color: ragStyle.red}}></i>
                            </div>
                            <div style={{float: 'left', paddingLeft: '5px'}}>Behind<br/> Schedule</div>
                        </div>
                        <div className={classes.btnGroup} style={{position: "relative", display: "inline-block", verticalAlign: "middle", padding: "5px 0 5px 5px"}}>
                            <div style={{float: 'left', paddingTop: '6px'}}>
                                <i className="fa fa-square" style={{color: ragStyle.amber}}></i>
                            </div>
                            <div style={{float: 'left', paddingLeft: '5px'}}>On<br/> Schedule</div>
                        </div>
                        <div className={classes.btnGroup} style={{position: "relative", display: "inline-block", verticalAlign: "middle", padding: "5px 0 5px 5px"}}>
                            <div style={{float: 'left', paddingTop: '6px'}}>
                                <i className="fa fa-square" style={{color: ragStyle.green}}></i>
                            </div>
                            <div style={{float: 'left', paddingLeft: '5px'}}>Ahead of<br/> Schedule</div>
                        </div>
                    </React.Fragment>
                );
                setPerformanceKey(performanceStyleKey);
            }
        }
        else {
            setPerformanceKey(null);
        }

        // Set the Milestones Key
        const milestonePerfKey = (
            <React.Fragment>
                <div className={classes.btnGroup} style={{position: "relative", display: "inline-block", verticalAlign: "middle", padding: "5px 0 5px 5px"}}>
                    <div style={{float: 'left', paddingTop: '5px'}}>
                        <i className="fa fa-square" style={{color: 'white', fontSize: '12px', backgroundRepeat: 'no-repeat', transform: 'rotate(45deg)', textShadow: '-1px 1px red, 1px 1px red, 1px -1px red, -1px -1px red'}}></i>
                    </div>
                    <div style={{float: 'left', paddingLeft: '6px', paddingTop: '5px'}}>Key Milestone</div>
                </div>
                <div className={classes.btnGroup} style={{position: "relative", display: "inline-block", verticalAlign: "middle", padding: "5px 0 5px 5px"}}>
                    <div style={{float: 'left', paddingTop: '6px'}}>
                        <i className="fa fa-square" style={{color: ragStyle.red, backgroundRepeat: 'no-repeat', transform: 'rotate(45deg)', textShadow: '0 0 black'}}></i>
                    </div>
                    <div style={{float: 'left', paddingLeft: '5px', paddingTop: '5px'}}>Overdue</div>
                </div>
                <div className={classes.btnGroup} style={{position: "relative", display: "inline-block", verticalAlign: "middle", padding: "5px 0 5px 5px"}}>
                    <div style={{float: 'left', paddingTop: '6px'}}>
                        <i className="fa fa-square" style={{color: ragStyle.amber, backgroundRepeat: 'no-repeat', transform: 'rotate(45deg)', textShadow: '0 0 black'}}></i>
                    </div>
                    <div style={{float: 'left', paddingLeft: '5px', paddingTop: '5px'}}>Upcoming</div>
                </div>
                <div className={classes.btnGroup} style={{position: "relative", display: "inline-block", verticalAlign: "middle", padding: "5px 0 5px 5px"}}>
                    <div style={{float: 'left', paddingTop: '6px'}}>
                        <i className="fa fa-square" style={{color: ragStyle.green, backgroundRepeat: 'no-repeat', transform: 'rotate(45deg)', textShadow: '0 0 black'}}></i>
                    </div>
                    <div style={{float: 'left', paddingLeft: '5px', paddingTop: '5px'}}>Complete</div>
                </div>
            </React.Fragment>
        );
        setMilestoneKey(milestonePerfKey);

        let initGantt = Gantt.getGanttInstance();

        initGantt.plugins({
            marker: true,
            tooltip: true
        });

        // Make Markers Sticky
        initGantt.attachEvent("onGanttScroll", function (left, top){
            let scroll_position = initGantt.getScrollState().y + 'px';
            let markers = document.querySelectorAll('.gantt_marker_content');
            if (markers) {
                for (let i = 0; i < markers.length; i++) {
                    markers[i].style.top = scroll_position;
                }
            }
        }, null);

        initGantt.config.xml_date = "%Y-%m-%d";

        // Create custom Key Milestone Border
        // https://docs.dhtmlx.com/gantt/desktop__task_types.html#creatingacustomtype
        // https://docs.dhtmlx.com/gantt/snippet/1b366ec0
        initGantt.templates.task_class = function(start, end, task){
            let css = [];
            if(task.type == initGantt.config.types.milestone) {
                if (task.nodetype.toLowerCase() === 'key milestone') {
                    css.push("milestone-key");
                }
            }

            return css.join(" ");
        };

        if (gridWidth) {
            initGantt.config.grid_width = gridWidth;
        }

        // TODO: Support Tree??
        // Columns in the Grid
        // TODO: Use Alias?? Currently label is in config and should suffice
        initGantt.config.columns = leftColumns;

        // initGantt.config.autofit = true;
        // initGantt.config.autosize = "xy";

        // Allow user column sorting
        initGantt.config.sort = true;

        initGantt.config.readonly = true;

        const zoomModule = initGantt.ext.zoom;

        zoomModule.init({
            levels: [
                {
                    name:"day",
                    scale_height: 27,
                    min_column_width:80,
                    scales:[
                        {unit: "day", step: 1, format: "%d %M"}
                    ]
                },
                {
                    name:"week",
                    scale_height: 50,
                    min_column_width:50,
                    scales:[
                        {unit: "week", step: 1, format: function (date) {
                                let dateToStr = initGantt.date.date_to_str("%d %M");
                                let endDate = initGantt.date.add(date, -6, "day");
                                let weekNum = initGantt.date.date_to_str("%W")(date);
                                return "#" + weekNum + ", " + dateToStr(date) + " - " + dateToStr(endDate);
                            }},
                        {unit: "day", step: 1, format: "%j %D"}
                    ]
                },
                {
                    name:"month",
                    scale_height: 50,
                    min_column_width:120,
                    scales:[
                        {unit: "month", format: "%F, %Y"},
                        {unit: "week", format: "Week #%W"}
                    ]
                },
                {
                    name:"quarter",
                    height: 50,
                    min_column_width:90,
                    scales:[
                        {unit: "month", step: 1, format: "%M"},
                        {
                            unit: "quarter", step: 1, format: function (date) {
                                let dateToStr = initGantt.date.date_to_str("%M");
                                let endDate = initGantt.date.add(initGantt.date.add(date, 3, "month"), -1, "day");
                                return dateToStr(date) + " - " + dateToStr(endDate);
                            }
                        }
                    ]},
                {
                    name:"year",
                    scale_height: 50,
                    min_column_width: 30,
                    scales:[
                        {unit: "year", step: 1, format: "%Y"}
                    ]}
            ],
            useKey: "ctrlKey",
            trigger: "wheel",
            element: function(){
                return document.querySelector(".gantt_task");
            }
        });
        zoomModule.setLevel(initialZoomLevel);

        // Add/Parse baselines
        initGantt.attachEvent("onTaskLoading", function (task) {
            task.planned_start_date = initGantt.date.parseDate(task.planned_start_date, "xml_date");
            task.planned_end_date = initGantt.date.parseDate(task.planned_end_date, "xml_date");
            return true;
        }, null);

        initGantt.attachEvent("onTaskSelected", function(taskId, e){
            var taskObj = initGantt.getTask(taskId);

            // console.log('onTaskSelected', taskId, taskObj);

            if (compConfig?.xFilter?.enabled) {
                // Push a filter into the stack
                // console.log("wbsgid eq {0}".format(taskObj[compConfig.xFilter.sourceField]));
                // TODO: Type check on data... this query will support numbers only
                dispatch(dataAddFilter(id, compConfig.xFilter.filterField, " eq {0}".format(taskObj[compConfig.xFilter.sourceField]), filterUpdateConditionAction.REPLACE, true, true));
            }
            return true;
        }, null);

        initGantt.attachEvent("onTaskClick", function(id,e){
            var target = e.target;

            var no_click = target.parentNode.parentNode.querySelector(".no_click");
            if (no_click) return false;

            return true;
        }, null);

        initGantt.attachEvent("onTaskOpened", function(taskId,e){
            var taskObj = initGantt.getTask(taskId);

            // console.log('Gantt:onTaskOpened() ' + taskObj.id);
            dispatch(persistGanttAddExpandedTask(taskObj.id));
            return true;
        }, null);

        initGantt.attachEvent("onTaskClosed", function(taskId,e){
            var taskObj = initGantt.getTask(taskId);

            // console.log('Gantt:onTaskClosed() ' + taskObj.id);
            dispatch(persistGanttRemoveExpandedTask(taskObj.id));
            return true;
        }, null);

        // Data Render Listener
        initGantt.attachEvent("onGanttScroll", function(){
            if (initGantt && initGantt.getScrollState()) {
                let sPos = initGantt.getScrollState();
                let posX = sPos.x;
                let posY = sPos.y

                const nowPos = initGantt.posFromDate(new Date());

                let ganttTask = document.querySelector(".gantt_task");
                if (ganttTask) {
                    const gridWidth = ganttTask.offsetWidth

                    if (nowPos < posX || nowPos > (posX + gridWidth)) {
                        setZoomTodayDisabled(false);
                    } else {
                        setZoomTodayDisabled(true);
                    }
                }
            }
        }, null);

        // Initialize Tooltips
        initGantt.attachEvent("onGanttReady", function(){
            let tooltips = initGantt.ext.tooltips;
            //tooltips.tooltip.setViewport(initGantt.$task_data);
            tooltips.tooltip.setViewport(ganttRef.current);
        }, null);

        // Task Tooltip
        initGantt.templates.tooltip_text = function(start,end,task){

            // Due to an issue we're terminating tooltips 4 seconds
            // after display AND when the route changes
            if (window.dhtmlxGanttHandle) {
                clearTimeout(window.dhtmlxGanttHandle);
            }
            window.dhtmlxGanttHandle = setTimeout(() => {
                initGantt.ext.tooltips.tooltip.hide();
            }, 4000)

            return "<b>Name:</b> " + getValueFromFormatters(getFieldFormatters(dataSource, nameField), task.text) +
                (task.id ? ("<br/><b>ID:</b> " + task.id) : '') +
                (task.nodetype ? ("<br/><b>Type:</b> " + getValueFromFormatters(getFieldFormatters(dataSource, nodeTypeField), task.nodetype)) : '') +
                (!task.unscheduled ? ("<br/><b>Start Date:</b> " + getValueFromFormatters(getFieldFormatters(dataSource, startDateField), initGantt.templates.tooltip_date_format(start))) : '') +
                (!task.unscheduled ? ("<br/><b>End Date:</b> " + getValueFromFormatters(getFieldFormatters(dataSource, endDateField), initGantt.templates.tooltip_date_format(end))) : '') +
                (task.planned_start_date ? ("<br/><b>Baseline Start Date:</b> " + getValueFromFormatters(getFieldFormatters(dataSource, plannedStartDateField), initGantt.templates.tooltip_date_format(task.planned_start_date))) : '') +
                (task.planned_end_date ? ("<br/><b>Baseline End Date:</b> " + getValueFromFormatters(getFieldFormatters(dataSource, plannedEndDateField), initGantt.templates.tooltip_date_format(task.planned_end_date))) : '') +
                (task.status ? ("<br/><b>Status:</b> " + getValueFromFormatters(getFieldFormatters(dataSource, statusField), task.status)) : '') +
                (progressField ? ("<br/><b>Percent Complete:</b> " + getValueFromFormatters(getFieldFormatters(dataSource, progressField), (task.progress ? (task.progress <= 1 ? task.progress * 100 : task.progress) : 0)) + "%") : '');
        };

        // Task Progress text and format
        initGantt.templates.progress_text = function (start, end, task) {
            return "<span style='text-align:left;'>" + Math.round(task.progress * 100) + "% </span>";
        };

        // // Grid row class
        // initGantt.templates.grid_row_class = function(start, end, task){
        //     if (task.gid === -2112)
        //         return "wheat_color";
        //     else
        //         return "";
        // };
        //
        // // Task row class
        // initGantt.templates.task_row_class = function(start, end, task){
        //     if (task.gid === -2112)
        //         return "wheat_color";
        //     else
        //     else
        //         return "";
        // };

        // Grid Hierarchy Open nodes
        initGantt.templates.grid_folder = function(item) {

            if (item.nodetype) {
                if (item.nodetype.toLowerCase() === 'project') {
                    // <AccountTreeOutlinedIcon fontSize="small"/>
                    return "<div style='margin: 4px 4px 0px 4px;'><svg height=\"20\" width=\"20\" viewBox=\"0 0 24 24\" focusable=\"false\" aria-hidden=\"true\" fill=\"" + (isThemeLight ? "black" : "white" ) + "\"><path d=\"M0 0h24v24H0z\" fill=\"none\"/><path d=\"M22 11V3h-7v3H9V3H2v8h7V8h2v10h4v3h7v-8h-7v3h-2V8h2v3h7zM7 9H4V5h3v4zm10 6h3v4h-3v-4zm0-10h3v4h-3V5z\"/></svg></div>"
                } else if (item.nodetype.toLowerCase() === 'wbs') {
                    // <FolderSpecialOutlinedIcon fontSize="small"/>
                    return "<div style='margin: 4px 4px 0px 4px;'><svg height=\"20\" width=\"20\" viewBox=\"0 0 24 24\" focusable=\"false\" aria-hidden=\"true\" fill=\"" + (isThemeLight ? "black" : "white" ) + "\"><path d=\"M0 0h24v24H0V0z\" fill=\"none\"/><path d=\"M20 6h-8l-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V6h5.17l2 2H20v10zm-6.92-3.96L12.39 17 15 15.47 17.61 17l-.69-2.96 2.3-1.99-3.03-.26L15 9l-1.19 2.79-3.03.26z\"/></svg></div>"
                } else if (item.$open) {
                    // <FolderOpenIcon fontSize="small"/>
                    return "<div style='margin: 4px 4px 0px 4px;'><svg height=\"20\" width=\"20\" viewBox=\"0 0 24 24\" focusable=\"false\" aria-hidden=\"true\" fill=\"" + (isThemeLight ? "black" : "white" ) + "\"><path d=\"M0 0h24v24H0z\" fill=\"none\"/><path d=\"M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z\"/></svg></div>"
                } else {
                    // <FolderOutlinedIcon fontSize="small"/>
                    return "<div style='margin: 4px 4px 0px 4px;'><svg height=\"20\" width=\"20\" viewBox=\"0 0 24 24\" focusable=\"false\" aria-hidden=\"true\" fill=\"" + (isThemeLight ? "black" : "white" ) + "\"><path d=\"M0 0h24v24H0z\" fill=\"none\"/><path d=\"M9.17 6l2 2H20v10H4V6h5.17M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z\"/></svg></div>"
                }
                // return "<div class='gantt_tree_icon gantt_folder_" + (item.$open ? "open" : "closed") + "'></div>";
            } else {
                if (item.$open) {
                    // <FolderOpenIcon fontSize="small"/>
                    return "<div style='margin: 4px 4px 0px 4px;'><svg height=\"20\" width=\"20\" viewBox=\"0 0 24 24\" focusable=\"false\" aria-hidden=\"true\" fill=\"" + (isThemeLight ? "black" : "white" ) + "\"><path d=\"M0 0h24v24H0z\" fill=\"none\"/><path d=\"M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z\"/></svg></div>"
                } else {
                    // <FolderOutlinedIcon fontSize="small"/>
                    return "<div style='margin: 4px 4px 0px 4px;'><svg height=\"20\" width=\"20\" viewBox=\"0 0 24 24\" focusable=\"false\" aria-hidden=\"true\" fill=\"" + (isThemeLight ? "black" : "white" ) + "\"><path d=\"M0 0h24v24H0z\" fill=\"none\"/><path d=\"M9.17 6l2 2H20v10H4V6h5.17M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z\"/></svg></div>"
                }
            }

        };

        // Grid Hierarchy Closed nodes
        initGantt.templates.grid_file = function(item) {
            if (item.nodetype) {
                if (item.nodetype.toLowerCase() === 'project') {
                    // <AccountTreeOutlinedIcon fontSize="small"/>
                    return "<div style='margin: 4px 4px 0px 4px;'><svg height=\"20\" width=\"20\" viewBox=\"0 0 24 24\" focusable=\"false\" aria-hidden=\"true\" fill=\"" + (isThemeLight ? "black" : "white" ) + "\"><path d=\"M0 0h24v24H0z\" fill=\"none\"/><path d=\"M22 11V3h-7v3H9V3H2v8h7V8h2v10h4v3h7v-8h-7v3h-2V8h2v3h7zM7 9H4V5h3v4zm10 6h3v4h-3v-4zm0-10h3v4h-3V5z\"/></svg></div>"
                } else if (item.nodetype.toLowerCase() === 'key milestone') {
                    return "<div style='margin-right: 6px;'><i class='fa fa-square' style='color: white; font-size: 12px; background-repeat: no-repeat; transform: rotate(45deg); text-shadow: -1px 1px red, 1px 1px red, 1px -1px red, -1px -1px red;'></i></div>"
                } else if (item.nodetype.toLowerCase() === 'milestone') {
                    return "<div style='margin-right: 6px;'><i class='fa fa-square' style='color: white; font-size: 12px; background-repeat: no-repeat; transform: rotate(45deg); text-shadow: -1px 1px black, 1px 1px black, 1px -1px black, -1px -1px black;'></i></div>"
                } else if (item.nodetype.toLowerCase() === 'wbs') {
                    // <FolderSpecialOutlinedIcon fontSize="small"/>
                    return "<div style='margin: 4px 4px 0px 4px;'><svg height=\"20\" width=\"20\" viewBox=\"0 0 24 24\" focusable=\"false\" aria-hidden=\"true\" fill=\"" + (isThemeLight ? "black" : "white" ) + "\"><path d=\"M0 0h24v24H0V0z\" fill=\"none\"/><path d=\"M20 6h-8l-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V6h5.17l2 2H20v10zm-6.92-3.96L12.39 17 15 15.47 17.61 17l-.69-2.96 2.3-1.99-3.03-.26L15 9l-1.19 2.79-3.03.26z\"/></svg></div>"
                }
                // return "<div class='gantt_tree_icon gantt_file'></div>";
            } else {
                // <FolderOutlinedIcon fontSize="small"/>
                return "<div style='margin: 4px 4px 0px 4px;'><svg height=\"20\" width=\"20\" viewBox=\"0 0 24 24\" focusable=\"false\" aria-hidden=\"true\" fill=\"" + (isThemeLight ? "black" : "white" ) + "\"><path d=\"M0 0h24v24H0z\" fill=\"none\"/><path d=\"M9.17 6l2 2H20v10H4V6h5.17M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z\"/></svg></div>"
            }
        };

        initGantt.config.show_tasks_outside_timescale = true;

        // https://docs.dhtmlx.com/gantt/api__gantt_gettask.html
        // Added this because LTC Tasks Feature showing Task ID could not be found error but the Tasks did show up
        initGantt.config.show_errors = false;

        // Set Tasks Visible
        const taskVisibleUserSelection = getUserSelections("tasksVisible");
        if (taskVisibleUserSelection !== undefined) {
            setTasksVisible(taskVisibleUserSelection !== undefined ? taskVisibleUserSelection : true);
        } else {
            if (toolbar?.commands?.displayTask?.checked === false) {
                setTasksVisible(false);
            } else {
                initGantt.init(ganttRef.current);
            }
        }

        setMyGantt(initGantt);
        // console.log('Gantt:useEffect(() => {}, []):End');

        return function cleanup() {
            // console.log('Gantt:Cleanup():Start');

            initGantt.ext.tooltips.tooltip.hide();

            var gridTimelineLayout = {
                css: 'gantt_container',
                rows:[
                    {
                        cols: [
                            {

                            }

                        ]
                    }
                ]
            };

            initGantt.config.layout = gridTimelineLayout;
            initGantt.resetLayout();
            initGantt.clearAll();

            // HACK: TODO: Check config to if we clear filter
            if (compConfig?.xFilter && compConfig?.xFilter?.enabled) {
                dispatch(dataRemoveFilter(id));
            }

            // console.log('Gantt:Cleanup():End');
        };
    }, []);

    useEffect(() => {
        // console.log('Gantt:useEffect(()=>{},[dataSource.data, milestonesVisible, myGantt, modalContent]): Start');

        // Since we have one gantt instance we need
        // to set it to the current container and render items
        // or re-render items when the modal content changes
        // TODO: Ensure we're the modal content
        if (myGantt) {
            myGantt.init(ganttRef.current);

            // Restore layout
            setupLayout(tasksVisible);

            // Restore baseline symbology
            displayBaselines(baselineVisible, baselineEnabled, myGantt);

            renderTasks();
            setUserSelections();
        }

        // console.log('Gantt:useEffect(()=>{},[dataSource.data, milestonesVisible, gantt, modalContent]): End');
    }, [dataSource.data, milestonesVisible, splitParentsVisible, myGantt, modalContent]);

    // Theme Changed
    useEffect(() => {
        // console.log('Gantt:useEffect(()=>{},[isThemeLight]): Start');

        // Summary Styling
        if (!isThemeLight) {
            const existingStyle = document.getElementById('dxhtmlSummaryDark');
            if (existingStyle) existingStyle.remove();
            const css = `
                .no-scale .gantt_layout_content {
                    background-color: rgb(31,31,31) !important;
                    background-image: linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05)) !important;
                }
                
                .no-scale .gantt_cell {
                    background-color: rgb(31,31,31) !important;
                    background-image: linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05)) !important;
                }
                `,
                head = document.head || document.getElementsByTagName('head')[0],
                style = document.createElement('style');
            style.setAttribute("id", "dxhtmlSummaryDark");
            style.type = 'text/css';
            head.appendChild(style);
            style.appendChild(document.createTextNode(css));
        }
        else {
            const existingStyle = document.getElementById('dxhtmlSummaryDark');
            if (existingStyle) existingStyle.remove();
        }

        // Task Elements
        const doesLightThemeExist = document.getElementById('dxhtmlLightTheme');
        const doesDarkThemeExist = document.getElementById('dxhtmlDarkTheme');

        if (isThemeLight && !doesLightThemeExist) {
            const existingStyle = document.getElementById('dxhtmlDarkTheme');
            if (existingStyle) existingStyle.remove();
            const css = `
                /* Progress Text */
                .gantt_task_progress{
                    text-align:left;
                    padding-left:10px;
                    box-sizing: border-box;
                    color: #FFF;
                    font-weight: bold;
                }
                `,
                head = document.head || document.getElementsByTagName('head')[0],
                style = document.createElement('style');
            style.setAttribute("id", "dxhtmlLightTheme");
            style.type = 'text/css';
            head.appendChild(style);
            style.appendChild(document.createTextNode(css));
        } else if (!isThemeLight && !doesDarkThemeExist) {
            const existingStyle = document.getElementById('dxhtmlLightTheme');
            if (existingStyle) existingStyle.remove();
            const css = `
                .gantt_container {
                    background-color: rgb(18,18,18);
                    background-image: linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05));
                    border-color: #000;
                }
                .gantt_task_row {
                    background-color: rgb(18,18,18) !important;
                    background-image: linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05)) !important;
                }
                .gantt_row {
                    background-color: rgb(18,18,18) !important;
                    background-image: linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05)) !important;
                }
                .gantt_grid_scale {
                    background-color: rgb(18,18,18) !important;
                    background-image: linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05)) !important;
                }
                .gantt_scale_line {
                    background-color: rgb(18,18,18) !important;
                    background-image: linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05)) !important;
                }
                /* Left Column Headers */
                .gantt_grid .gantt_grid_head_cell {
                    color: #FFF !important
                }
                /* Date/Scale Text Above Tasks */
                .gantt_task .gantt_task_scale .gantt_scale_cell {
                    color: #FFF !important
                }
                /* Task Text */
                .gantt_grid_data .gantt_cell {
                    color: #FFF !important
                }
                .gantt_task_content {
                    color: #FFF !important
                }
                /* Progress Text */
                .gantt_task_progress{
                    text-align:left;
                    padding-left:10px;
                    box-sizing: border-box;
                    color: #FFF;
                    font-weight: bold;
                }
                `,
                head = document.head || document.getElementsByTagName('head')[0],
                style = document.createElement('style');
            style.setAttribute("id", "dxhtmlDarkTheme");
            style.type = 'text/css';
            head.appendChild(style);
            style.appendChild(document.createTextNode(css));
        }

        toolbarStyle = {
            backgroundColor: isThemeLight ? "#F2F4F5" : "rgba(125, 125, 125, 0.2)"
        };

		if (myGantt) {
			myGantt.init(ganttRef.current);

			// Grid Hierarchy Open nodes
			myGantt.templates.grid_folder = function (item) {

				if (item.nodetype) {
					if (item.nodetype.toLowerCase() === 'project') {
						// <AccountTreeOutlinedIcon fontSize="small"/>
						return "<div style='margin: 4px 4px 0px 4px;'><svg height=\"20\" width=\"20\" viewBox=\"0 0 24 24\" focusable=\"false\" aria-hidden=\"true\" fill=\"" + (isThemeLight ? "black" : "white") + "\"><path d=\"M0 0h24v24H0z\" fill=\"none\"/><path d=\"M22 11V3h-7v3H9V3H2v8h7V8h2v10h4v3h7v-8h-7v3h-2V8h2v3h7zM7 9H4V5h3v4zm10 6h3v4h-3v-4zm0-10h3v4h-3V5z\"/></svg></div>"
					} else if (item.nodetype.toLowerCase() === 'wbs') {
						// <FolderSpecialOutlinedIcon fontSize="small"/>
						return "<div style='margin: 4px 4px 0px 4px;'><svg height=\"20\" width=\"20\" viewBox=\"0 0 24 24\" focusable=\"false\" aria-hidden=\"true\" fill=\"" + (isThemeLight ? "black" : "white") + "\"><path d=\"M0 0h24v24H0V0z\" fill=\"none\"/><path d=\"M20 6h-8l-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V6h5.17l2 2H20v10zm-6.92-3.96L12.39 17 15 15.47 17.61 17l-.69-2.96 2.3-1.99-3.03-.26L15 9l-1.19 2.79-3.03.26z\"/></svg></div>"
					} else if (item.$open) {
						// <FolderOpenIcon fontSize="small"/>
						return "<div style='margin: 4px 4px 0px 4px;'><svg height=\"20\" width=\"20\" viewBox=\"0 0 24 24\" focusable=\"false\" aria-hidden=\"true\" fill=\"" + (isThemeLight ? "black" : "white") + "\"><path d=\"M0 0h24v24H0z\" fill=\"none\"/><path d=\"M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z\"/></svg></div>"
					} else {
						// <FolderOutlinedIcon fontSize="small"/>
						return "<div style='margin: 4px 4px 0px 4px;'><svg height=\"20\" width=\"20\" viewBox=\"0 0 24 24\" focusable=\"false\" aria-hidden=\"true\" fill=\"" + (isThemeLight ? "black" : "white") + "\"><path d=\"M0 0h24v24H0z\" fill=\"none\"/><path d=\"M9.17 6l2 2H20v10H4V6h5.17M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z\"/></svg></div>"
					}
					// return "<div class='gantt_tree_icon gantt_folder_" + (item.$open ? "open" : "closed") + "'></div>";
				} else {
					if (item.$open) {
						// <FolderOpenIcon fontSize="small"/>
						return "<div style='margin: 4px 4px 0px 4px;'><svg height=\"20\" width=\"20\" viewBox=\"0 0 24 24\" focusable=\"false\" aria-hidden=\"true\" fill=\"" + (isThemeLight ? "black" : "white") + "\"><path d=\"M0 0h24v24H0z\" fill=\"none\"/><path d=\"M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z\"/></svg></div>"
					} else {
						// <FolderOutlinedIcon fontSize="small"/>
						return "<div style='margin: 4px 4px 0px 4px;'><svg height=\"20\" width=\"20\" viewBox=\"0 0 24 24\" focusable=\"false\" aria-hidden=\"true\" fill=\"" + (isThemeLight ? "black" : "white") + "\"><path d=\"M0 0h24v24H0z\" fill=\"none\"/><path d=\"M9.17 6l2 2H20v10H4V6h5.17M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z\"/></svg></div>"
					}
				}

			};

			// Grid Hierarchy Closed nodes
			myGantt.templates.grid_file = function (item) {
				if (item.nodetype) {
					if (item.nodetype.toLowerCase() === 'project') {
						// <AccountTreeOutlinedIcon fontSize="small"/>
						return "<div style='margin: 4px 4px 0px 4px;'><svg height=\"20\" width=\"20\" viewBox=\"0 0 24 24\" focusable=\"false\" aria-hidden=\"true\" fill=\"" + (isThemeLight ? "black" : "white") + "\"><path d=\"M0 0h24v24H0z\" fill=\"none\"/><path d=\"M22 11V3h-7v3H9V3H2v8h7V8h2v10h4v3h7v-8h-7v3h-2V8h2v3h7zM7 9H4V5h3v4zm10 6h3v4h-3v-4zm0-10h3v4h-3V5z\"/></svg></div>"
					} else if (item.nodetype.toLowerCase() === 'key milestone') {
						return "<div style='margin-right: 6px;'><i class='fa fa-square' style='color: white; font-size: 12px; background-repeat: no-repeat; transform: rotate(45deg); text-shadow: -1px 1px red, 1px 1px red, 1px -1px red, -1px -1px red;'></i></div>"
					} else if (item.nodetype.toLowerCase() === 'milestone') {
						return "<div style='margin-right: 6px;'><i class='fa fa-square' style='color: white; font-size: 12px; background-repeat: no-repeat; transform: rotate(45deg); text-shadow: -1px 1px black, 1px 1px black, 1px -1px black, -1px -1px black;'></i></div>"
					} else if (item.nodetype.toLowerCase() === 'wbs') {
						// <FolderSpecialOutlinedIcon fontSize="small"/>
						return "<div style='margin: 4px 4px 0px 4px;'><svg height=\"20\" width=\"20\" viewBox=\"0 0 24 24\" focusable=\"false\" aria-hidden=\"true\" fill=\"" + (isThemeLight ? "black" : "white") + "\"><path d=\"M0 0h24v24H0V0z\" fill=\"none\"/><path d=\"M20 6h-8l-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V6h5.17l2 2H20v10zm-6.92-3.96L12.39 17 15 15.47 17.61 17l-.69-2.96 2.3-1.99-3.03-.26L15 9l-1.19 2.79-3.03.26z\"/></svg></div>"
					}
					// return "<div class='gantt_tree_icon gantt_file'></div>";
				} else {
					// <FolderOutlinedIcon fontSize="small"/>
					return "<div style='margin: 4px 4px 0px 4px;'><svg height=\"20\" width=\"20\" viewBox=\"0 0 24 24\" focusable=\"false\" aria-hidden=\"true\" fill=\"" + (isThemeLight ? "black" : "white") + "\"><path d=\"M0 0h24v24H0z\" fill=\"none\"/><path d=\"M9.17 6l2 2H20v10H4V6h5.17M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z\"/></svg></div>"
				}
			};

			setMyGantt(myGantt);
		}

        // console.log('Gantt:useEffect(()=>{},[isThemeLight]): End');
    }, [isThemeLight]);

    // Toggle Task Visibility
    useEffect(() => {
        // console.log('Gantt:useEffect(()=>{},[tasksVisible]): Start');
        if (myGantt) {
            setupLayout(tasksVisible);
        }

        // console.log('Gantt:useEffect(()=>{},[tasksVisible]): End');
    }, [tasksVisible]);

	// Did Component Wrapper Trigger Redux State Change?
	useEffect(() => {
		if (componentWrapperEdit.parentId && componentWrapperEdit.childId && xId) {
			if ((componentWrapperEdit.parentId === xId) || (componentWrapperEdit.childId === xId)) {
				const page = pages.find(page => page.name === pageID);
				for (let i = 0; i < pageConfigs.length; i++ ) {
					const roleConfig = page.roles.find(r => r.name === activeRole)[pageConfigs[i]];
					if (roleConfig) {
						const findResult = findComponent(xId, componentWrapperEdit.childId === xId ? componentWrapperEdit.parentId : null, roleConfig.configuration.components);
						if (findResult) {
							setCompConfig(findResult.configuration);
							break;
						}
					}
				}
			}
		}
	},[componentWrapperEdit]);

	// Component Configuration (compConfig) changed, reset state
	useEffect(() => {
		setTitle(compConfig.name?.title ? compConfig.name.title : '');
		setSubTitle(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	},[compConfig]);

	useEffect(() => {
		resizeCard();
	},[title, subtitle]);

	// componentUpdated & Resized
	useEffect(() => {
		if (xId) {
			if (componentUpdated.id === xId) {
				setCompConfig(componentUpdated.config);
			}
		}
	}, [componentUpdated]);

	// xEdit
	useEffect(() => {
		if (editXId) {
			if ((editXId === xId) && (pageID)) {

				const placeholders = {
					title: title,
					subtitle: subtitle
				}

				const settingsConfig = {
					title: 'Gantt Configuration',
					parent: {
						minWidth: '400px',
						maxWidth: '400px',
						minHeight: '520px',
						maxHeight: '520px'
					},
					paper: null,
					button: null,
					content: <SettingsForm pageID={pageID} xId={xId} configuration={compConfig} schemaType={'Gantt'} placeholders={placeholders}/>,
					tooltipContent: null
				}
				dispatch(appSetModalContent(settingsConfig));
			}
		}
	},[editXId, pageID]);

    const setupLayout = () => {
        // Setup summary based on the configured columns
        // NOTE: In future we may want to define what fields are summarized
        // and potentially a new summary grid i.e. # projects however for the time
        // being we do not
        const summaryColumns = myGantt.config.columns.map(c =>
        {
            const newColumn = {
                name: c.name,
                label: c.label,
                align: c.align,
                min_width: c.min_width,
                width: c.width,
                template: function(task) {
                    // console.log('Gantt:SummaryGrid:template(task)');

                    let returnValue = '';

                    if (!task.$index) {
                        var tasks = myGantt.getTaskByTime();
                        var parentTasks = tasks.filter(t => t.parent === 0);

                        if (parentTasks.length === 1) {
                            const rawValue = parentTasks[0]["{0}_raw".format(c.name)];
                            if (isNaN(rawValue)) {
                                returnValue = null;
                            } else {
                                returnValue = parentTasks[0][c.name];
                            }
                        } else {

                            // Numeric
                            if (c.name.toLowerCase().indexOf("cpi") < 0 && c.name.toLowerCase().indexOf("spi") < 0) {
                                const summarizedValue = parentTasks.reduce((a, b) => a + (b["{0}_raw".format(c.name)] || 0), 0);

                                if (isNaN(summarizedValue))
                                    return null;

                                const mockO = {};
                                mockO[c.name] = summarizedValue;

                                const formattedValue = getFormattedValue(mockO, c.name);

                                returnValue = formattedValue;

                            } else if (c.name.toLowerCase().indexOf("cpi") >= 0 ) {
                                // CPI Custom - HC Dependency
                                let cpi_c = null;

                                try {
                                    const summarizedActual = parentTasks.reduce((a, b) => a + (b["{0}_raw".format("periodactualtotalcost_c")] || 0), 0);
                                    const summarizedEarned = parentTasks.reduce((a, b) => a + (b["{0}_raw".format("periodearnedvaluecost_c")] || 0), 0);

                                    if (summarizedActual && summarizedActual !== 0 && summarizedEarned) {
                                        cpi_c = summarizedEarned / summarizedActual;
                                    } else {
                                        cpi_c = null;
                                    }

                                } catch (e) {
                                    console.log("**ERROR**", e);
                                }

                                const mockO = {};
                                mockO[c.name] = cpi_c;

                                const formattedValue = getFormattedValue(mockO, c.name);

                                returnValue = formattedValue;

                            } else if (c.name.toLowerCase().indexOf("spi") >= 0 ) {
                                // SPI Custom - HC Dependency
                                let spi_c = null;

                                try {
                                    const summarizedPlanned = parentTasks.reduce((a, b) => a + (b["{0}_raw".format("periodplannedvaluecost_c")] || 0), 0);
                                    const summarizedEarned = parentTasks.reduce((a, b) => a + (b["{0}_raw".format("periodearnedvaluecost_c")] || 0), 0);

                                    if (summarizedPlanned && summarizedPlanned !== 0 && summarizedEarned) {
                                        spi_c = summarizedEarned / summarizedPlanned;
                                    } else {
                                        spi_c = null;
                                    }

                                } catch (e) {
                                    console.log("**ERROR**", e);
                                }

                                const mockO = {};
                                mockO[c.name] = spi_c;

                                const formattedValue = getFormattedValue(mockO, c.name);

                                returnValue = formattedValue;
                            }
                        }
                    }
                    else returnValue = '';


                    return '<span class="no_click">{0}</span>'.format(returnValue ? returnValue : '');
                }
            }
            return newColumn;
        });

        let ganttSummaryLayout = {
            columns: summaryColumns
        }

        let gridTimelineLayout = {
            css: 'gantt_container',
            rows:[
                {
                    cols: [
                        {
                            width: 300,
                            rows:[
                                {view: "grid", id: "grid", scrollable: true, scrollX:"gridScrollHor", scrollY:"scrollVer", css: "gantt-content-area"},
                                {view: "scrollbar", scroll: "x", id:"gridScrollHor", height:20}
                            ]
                        },
                        {resizer: false, width: 1},
                        {
                            rows:[
                                {view: "timeline", id: "timeline", scrollX:"scrollHor", scrollY:"scrollVer"},
                                {view: "scrollbar", scroll: "x", visible: true, id:"scrollHor", height:20},

                            ]},
                        {view: "scrollbar", scroll: "y", id:"scrollVer"}

                    ]
                },
                {
                    gravity: 0,
                    height: 40,
                    cols:[
                        {view: "grid", id:"summary", scrollable: true, scrollX:"gridScrollHor", bind: "task", css: "no-scale",  config: ganttSummaryLayout, width: 300},
                        {resizer: false, width: 1},
                        {css: "no-scale"},
                    ]},
            ]
        };

        let gridOnlyLayout = {
            css: 'gantt_container',
            rows:[
                {
                    cols: [
                        {
                            rows:[
                                {
                                    gravity: 3,
                                    cols:[
                                        {view: "grid", id: "grid", scrollable: true, scrollX:"gridScrollHor", scrollY:"scrollVer", css: "gantt-content-area"}
                                        ]
                                },
                                {
                                    gravity: 0,
                                    height: 40,
                                    cols:[
                                        {view: "grid", id: "summary", scrollable: true, scrollX:"gridScrollHor", bind: "task", css: "no-scale", config: ganttSummaryLayout}
                                    ]
                                }
                            ]
                        },
                        {view: "scrollbar", scroll: "y", id:"scrollVer"}
                    ]
                },
                {view: "scrollbar", scroll: "x", id:"gridScrollHor"}
            ]
        };

        if (tasksVisible) {
            myGantt.config.layout = gridTimelineLayout
        }
        else {
            myGantt.config.layout = gridOnlyLayout
        }

        // Set the correct gr
        let targetWidth = 0;
        for (let col of myGantt.config.columns) {
            targetWidth += col.width;
        }

        // Support for single columns / make grid fit 100% width
        if (targetWidth < ganttRef.current.clientWidth)
            targetWidth = ganttRef.current.clientWidth;

        const existingStyle = document.getElementById('dxhtmlScrollFix');
        if (existingStyle) existingStyle.remove();
        const css = `
                .gantt_grid_data{
                    width: {0}px !important;
                }
                .gantt_grid_scale{
                    width: {0}px !important;
                }
                .gantt_grid_scale:after {
                    width: {0}px !important;
                }
                `.format(targetWidth),
            head = document.head || document.getElementsByTagName('head')[0],
            style = document.createElement('style');
        style.setAttribute("id", "dxhtmlScrollFix");
        style.type = 'text/css';
        head.appendChild(style);
        style.appendChild(document.createTextNode(css));

        if (window.ganttInitHandle) {
            clearTimeout(window.ganttInitHandle);
            window.ganttInitHandle = null;
        }

        const ganttInitHandle = setTimeout(() => {
            if (myGantt) {
                myGantt.init(ganttRef.current);

                if (tasksVisible) {
                    // Add custom behavior after init
                    addTaskLayerMapToGantt();
                    myGantt.render();
                }
            }
        }, 400);

        window.ganttInitHandle = ganttInitHandle;
    }

    // Add custom behavior after the init
    const addTaskLayerMapToGantt = () => {
        // console.log('Gantt:addTaskLayerMapToGantt(): Start');

        if (myGantt) {

            myGantt.addTaskLayer(function draw_planned(task) {

                if (task.planned_start_date && task.planned_end_date && (task.type.toLowerCase() === 'milestone')) {
                    let sizes = myGantt.getTaskPosition(task, task.planned_start_date, task.planned_end_date);
                    let el = document.createElement('div');
                    el.className = 'baselineMilestone';
                    el.style.left = sizes.left + 'px';
                    el.style.width = sizes.width + 'px';
                    el.style.top = sizes.top + myGantt.config.bar_height + 13 + 'px';
                    return el;
                } else if (task.planned_start_date && task.planned_end_date) {
                    let sizes = myGantt.getTaskPosition(task, task.planned_start_date, task.planned_end_date);
                    let el = document.createElement('div');
                    el.className = 'baseline';
                    el.style.left = sizes.left + 'px';
                    el.style.width = sizes.width + 'px';
                    el.style.top = sizes.top + myGantt.config.bar_height + 13 + 'px';
                    return el;
                }

                return false;
            });
        }
    }

    const getFormattedValue = (d, fieldName) => {
        // console.log('Gantt:getFormattedValue(): Start');

        let formattedValue = d[fieldName];
        const fieldFormatters = getFieldFormatters(dataSource, fieldName);
        for (const formatter of fieldFormatters) {
            if (formatter.type.toLowerCase() === 'date') {
                formattedValue = formatDate(formattedValue, formatter.config)
            } else if (formatter.type.toLowerCase() === 'number') {
                formattedValue = formatNumber(formattedValue, formatter.config);
            } else if (formatter.type.toLowerCase() === 'style') {
                formattedValue = formatStyle(formattedValue, formatter.config);
            } else if (formatter.type.toLowerCase() === 'text') {
                formattedValue = formatText(formattedValue, formatter.config);
            } else if (formatter.type.toLowerCase() === 'suffix') {
                formattedValue = formatSuffix(formattedValue, formatter.config);
            } else if (formatter.type.toLowerCase() === 'goodbad') {
                formattedValue = formatGoodBad(formattedValue, formatter.config, formattedValue, formatter.condition);
            } else {
                // Something else
            }
        }

        return formattedValue;
    }

    const getValueFromFormatters = (formatters, input) => {

        // console.log('{0}'.format('--> Gantt: getValueFromFormatters()'));
        // console.log('FORMATTERS: ', formatters);
        // console.log('INPUT: {0} ({1})'.format(input, typeof input));

        let formattedValue = input;
        if (formatters) {
            for (const formatter of formatters) {
                if (formatter.type.toUpperCase() === 'AVATAR') {
                    formattedValue = formatAvatar(formattedValue, formatter.config);
                } else if (formatter.type.toUpperCase() === 'DATE') {
                    formattedValue = formatDate(formattedValue, formatter.config);
                } else if (formatter.type.toUpperCase() === 'NUMBER') {
                    formattedValue = formatNumber(formattedValue, formatter.config);
                } else if (formatter.type.toUpperCase() === 'STYLE') {
                    formattedValue = formatStyle(formattedValue, formatter.config, input, formatter.condition);
                } else if (formatter.type.toUpperCase() === 'TEXT') {
                    formattedValue = formatText(formattedValue, formatter.config);
                } else if (formatter.type.toUpperCase() === 'LINK') {
                    formattedValue = FormatLink(formattedValue, formatter.config);
                } else if (formatter.type.toUpperCase() === 'MODAL') {
                    formattedValue = FormatModalLink(formattedValue, formatter.config);
                } else if (formatter.type.toUpperCase() === 'SUFFIX') {
                    formattedValue = formatSuffix(formattedValue, formatter.config);
                } else if (formatter.type.toUpperCase() === 'GOODBAD') {
                    formattedValue = formatGoodBad(formattedValue, formatter.config, input, formatter.condition);
                } else {

                }
            }
        }

        return formattedValue;
    }

    const getParentTasks = (parentLevel, currLevel, allRollups, level) => {
        // console.log('getParentTasks');
        // console.log(parentLevel);
        // console.log(currLevel);
        // console.log(allRollups);
        // console.log(level);

        let rollupTasks = [];

        if (parentLevel.length > 0) {
            for (let i = 0; i < parentLevel.length; i++) {
                let p = parentLevel[i];

                for (let z = 0; z < currLevel.text.length; z++) {
                    let c = currLevel.text[z];
                    let currField = currLevel.field;

                    for (let x = 0; x < dataSource.data.length; x++) {
                        let d = dataSource.data[x];
                        if (d[p.field] === p.text && d[currField] === c) {

                            const parentTasks = allRollups.filter(f => {
                                return f.field === p.field && f.text === p.text
                            });

                            for (let q = 0; q < parentTasks.length; q++) {
                                const grandParentTask = allRollups.find(f => {
                                    return ((f.id === parentTasks[q].parent) || (parentTasks[q].parent === null && f.field === parentTasks[q].field && f.text === parentTasks[q].text))
                                });

                                if (grandParentTask) {
                                    if (d[grandParentTask.field] === grandParentTask.text && p.id === parentTasks[q].id) {
                                        let task = {
                                            id: "r" + level + i + x,
                                            level: level,
                                            field: currField,
                                            text: c,
                                            gid: null,
                                            parent: parentTasks[q].id,
                                            type: nodeTypeField ? (d[nodeTypeField].toLowerCase().match(/milestone/g) ? myGantt.config.types.milestone : myGantt.config.types.task) : myGantt.config.types.task
                                        }

                                        // Build Left Column value from config
                                        for (let l = 0; l < leftColumns.length; l++) {
                                            task[leftColumns[l].name] = d[leftColumns[l].tree === true ? currField : leftColumns[l].name];
                                        }

                                        rollupTasks = rollupTasks.concat(task);
                                    }
                                }
                            }
                            break;  // Break the loop, did what we needed, don't want dupes
                        }
                    }
                }
            }
        } else {
            // Create Top Level rollup tasks
            const rTasks = currLevel.text.map((t, tIndex) => {
                // Build First Left Column value from config
                let taskProperty = {};
                taskProperty[leftColumns[0].name] = t;

                return {
                    ...taskProperty,
                    id: "r0" + tIndex,
                    level: 0,
                    field: currLevel.field,
                    text: t,
                    gid: null,
                    parent: null,
                    type: myGantt.config.types.project
                }
            })

            rollupTasks = rollupTasks.concat(rTasks);
        }

        return rollupTasks
    }

    const getNestedChildrenTasks = (items, parent = 0) => {
        // http://oskarhane.com/create-a-nested-array-recursively-in-javascript/
        // https://gist.github.com/kalinchernev/1db8f0a2176ddf55f9c08894a413a927

        // console.log('getNestedChildrenTasks');

        const nested = [];

        Object.values(items).forEach(item => {
            // parent can be a string or a number
            /* eslint-disable-next-line eqeqeq */
            if (item.parent == parent) {
                // const children = getNestedChildrenTasks(items, item.id);

                // if (children.length) {

                    // console.log('children ---------------->', children);

                    // Get Rollup start_date
                    // const minDate = getParentTaskRollupValues(children, 'minDate', 'start_date');

                    // Get Rollup end_date
                    // const maxDate = getParentTaskRollupValues(children, 'maxDate', 'end_date');


                    /* eslint-disable-next-line no-param-reassign */
                    // item.children = children;
                    // item.start_date = DateUtilities.dateStringForQuery(minDate);
                    // item.end_date = DateUtilities.dateStringForQuery(maxDate);
                // }

                nested.push(item);
            }
        });

        return nested;

    }

    // const getParentTaskRollupValues = (items, type, field) => {
    //     console.log('getParentTaskRollupValues');
    //
    //     let retVal = null;
    //     let newVal = null;
    //
    //     if (type === 'minDate') {
    //
    //         retVal = new Date(Math.min.apply(null, items.map(function(e) {
    //             if (!e.hasOwnProperty(field)) {
    //                 const grandChildren = getNestedChildrenTasks(e.children, e.id);
    //                 newVal = getParentTaskRollupValues(grandChildren, 'minDate', 'start_date');
    //             } else {
    //                 let newDate = new Date(e[field]);
    //                 newVal = new Date(newDate.getUTCFullYear(), newDate.getUTCMonth(), newDate.getUTCDate());
    //             }
    //             return newVal
    //         })));
    //
    //     } else if (type === 'maxDate') {
    //
    //         retVal = new Date(Math.max.apply(null, items.map(function(e) {
    //             if (!e.hasOwnProperty(field)) {
    //                 const grandChildren = getNestedChildrenTasks(e.children, e.id);
    //                 newVal = getParentTaskRollupValues(grandChildren, 'maxDate', 'end_date');
    //             } else {
    //                 let newDate = new Date(e[field]);
    //                 newVal = new Date(newDate.getUTCFullYear(), newDate.getUTCMonth(), newDate.getUTCDate());
    //             }
    //             return newVal
    //         })));
    //
    //     } else {
    //         retVal = null;
    //     }
    //
    //     return retVal
    // }

    const renderTasks = () => {
        if (dataSource.data && dataSource.fetched) {

            // Task Data
            // console.log(dataSource.data);

            // If our dependencies are not met then we're
            // loading/no chance until they are
            if (!areLoadDependenciesMet(dependencies, filters)) {
                return;
            }

            // Build Dynamic Parent Tasks
            let fieldRollups = [];
            let rollupTasks = [];
            if (rollupConfig.enabled) {
                if (rollupConfig.type.toLowerCase() === 'dynamic') {

                    // Setup Rollups per Field
                    fieldRollups = rollupConfig.fields.map( (r, rIndex) => {
                        const fieldValues = [...new Set( dataSource.data.map(n => (n[r]) ))]
                        return {
                            id: rIndex,
                            field: r,
                            text: fieldValues.filter(n => {return n !== null;})  // Remove NULLs
                        }
                    })

                    // Build Lower Level tasks
                    let parentTasks = []
                    for (let i = 0; i < fieldRollups.length; i++) {
                        let level = i;

                        parentTasks = getParentTasks(parentTasks, fieldRollups[i], rollupTasks, level);
                        rollupTasks = rollupTasks.concat(parentTasks);
                    }

                }
            }

            // Get the date in a supported format i.e. UNC to %Y-%m-%d
            // And mapped to core fields... this can be configured
            let tasks = [];
            for (let i  = 0; i < dataSource.data.length; i++) {
                const d = dataSource.data[i];

                // TODO: Logic based on values
                let diffDays = 0;
                const startDate = Date.parse(d[startDateField]);
                const endDate = Date.parse(d[endDateField]);

                if (typeof startDate === 'number' &&
                    typeof endDate === 'number') {
                    const diffTime = Math.abs(endDate - startDate);
                    diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
                }

                // Setup Task configuration

                // Gantt Layout taskType will equal milestone if 0 day duration AND nodeType (or milestone...from Tasks Feature) is like milestone, else it will display as a task
                const taskType = (diffDays === 0 && (d[nodeTypeField] !== undefined ? d[nodeTypeField].toLowerCase().match(/milestone/g) : d.milestone === true)) ? myGantt.config.types.milestone : myGantt.config.types.task;

                const task = {
                    ...d,
                    id: d[idField] ? d[idField] : i,
                    type: taskType,
                    start_date: d[startDateField] ? d[startDateField].toString().substr(0, 10) : null,
                    end_date: d[endDateField] ? d[endDateField].toString().substr(0, 10) : null,
                    planned_start_date: d[plannedStartDateField] ? d[plannedStartDateField].toString().substr(0, 10) : null,
                    planned_end_date: d[plannedEndDateField] ? d[plannedEndDateField].toString().substr(0, 10): null,
                    duration: diffDays,
                    progress: d[progressField] ? (d[progressField] > 1 ? d[progressField] / 100 : d[progressField]) : 0,
                    status: d[statusField],
                    nodetype: d[nodeTypeField],
                    rolledUp: d[nodeTypeField] === 'EPS' ? false : true,
                    text: d[nameField],
                    // TODO: Style based on theme
                    textColor: '#000',
                    // TODO: Style based on status/formatter
                    color: getTaskPerformanceStyle(d, taskType), // '#F4A460'
                    unscheduled: d[startDateField] ? false : true
                }

                // Assign Parents to Tasks by Looping through Rollup Tasks in reverse Level order, to rollup from bottom
                if (rollupConfig.enabled) {
                    if (rollupConfig.type.toLowerCase() === 'dynamic') {
                        for (let i = rollupTasks.length - 1; i >= 0; i--) {
                            const r = rollupTasks[i];
                            // Does Task have a defined Rollup field value === Rollup Task?
                            if (task[r.field] === r.text) {
                                if (r.parent !== null) {
                                    const parentTask = rollupTasks.find(f => {
                                        return f.id === r.parent
                                    });
                                    if (task[parentTask.field] === parentTask.text) {
                                        task.parent = r.id;
                                        break;
                                    }
                                } else {
                                    // NULL Parent means only one level (0) of Parents exist
                                    task.parent = r.id;
                                }
                            }
                        }
                    }
                }

                // If Milestones deselected, do not show them
                if (task.duration === 0 && (task.type === myGantt.config.types.milestone) && !milestonesVisible) {

                }
                else {
                    tasks.push(
                        task
                    );
                }
            }

            // Should we show the Milestone key in the Toolbar?
            const milestoneFound = tasks.find(m => m.type === myGantt.config.types.milestone);
            setShowMilestoneKey(milestoneFound ? true : false);

            // Add Dynamic Rollup Tasks
            if (rollupConfig.enabled) {
                if (rollupConfig.type.toLowerCase() === 'dynamic') {

                    // Unschedule Parents where Children have NULL Start_date and End_Date
                    const initialTasks = tasks.concat(rollupTasks);
                    // Loop through Rollup Levels
                    for (let i = fieldRollups.length - 1; i >= 0; i--) {
                        //console.log('level: ', i);

                        // Get Rollup Tasks for Level
                        let levelRollups = rollupTasks.filter(f => f.level === i);

                        // For Each Rollup Task at this Level, get the children
                        let nestedTasks = levelRollups.map((m) => {
                            const children = getNestedChildrenTasks(initialTasks, m.id);

                            // Do Start/End Dates exist OR has Unscheduled prop been deliberately set to FALSE, in any child task?
                            let childStart = children.find((s) => {
                                if (((s[startDateField]) && (s[endDateField])) || (s.unscheduled === false)) {
                                    return s
                                }
                            });

                            // If child tasks have Start/End Dates or Unscheduled prop === false, then set to Unscheduled and don't show dates
                            if (childStart === undefined) {
                                m.unscheduled = true;
                            } else {
                                m.unscheduled = false;
                            }

                            return m;
                        });
                        tasks = tasks.concat(nestedTasks);
                    }

                // Data has the Parents
                } else if (rollupConfig.type.toLowerCase() === 'tasks') {
                    let parents = [];
                    for (let i = 0; i < tasks.length; i++) {

                        // If Parent exists, assign it, otherwise NULL because myGantt will not render
                        const theParent = tasks.find(d => {
                            return d.id === tasks[i][rollupConfig.parent]
                        });

                        tasks[i].parent = theParent ? tasks[i][rollupConfig.parent] : null;

                        if (theParent) { // Parent Found
                            if (parents.indexOf(theParent) === -1) { // Has Parent been pushed yet?
                                parents.push(theParent);
                            }
                        }
                    }

                    // If Parent's Children need Split to one row ...
                    if (rollupConfig?.splitParents != undefined) {
                        if (rollupConfig.splitParents === true && splitParentsVisible) {
                            for (let i = 0; i < parents.length; i++) {
                                for (let t = 0; t < tasks.length; t++) {
                                    if (parents[i].id === tasks[t].id) {
                                        tasks[t].render = "split";
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // Loop through EPS and summarize projects (sigh)
            rollupHierarchy(tasks);

            // Add formatted column fields
            for (const task of tasks) {
                for (let c = 0; c < leftColumns.length; c++) {
                    // Raw values
                    task["{0}_raw".format(leftColumns[c].name)] = task[leftColumns[c].name];
                    // Formatted Values
                    task[leftColumns[c].name] = getFormattedValue(task, leftColumns[c].name);
                }
            }

            const data = {
                "tasks": tasks,
                "links": []
            };

            // Show / Hide Progress (Percent Complete)
            myGantt.config.show_progress = progressVisible ? true : false;
            myGantt.config.show_unscheduled = true;

            myGantt.clearAll();

            // Add NOW Marker
            const dateToStr = myGantt.date.date_to_str(myGantt.config.task_date);
            myGantt.addMarker({
                start_date: new Date(), // Date object that sets the marker's date
                css: "today", // CSS class applied to the marker
                text: "Now", // Marker title
                title: dateToStr( new Date()) // the marker's tooltip
            });

            // Add DATA DATE Marker
            if (displayDataDate) {
                myGantt.addMarker({
                    start_date: new Date(dataDate), //new Date(), // Date object that sets the marker's date
                    css: "status_line", // CSS class applied to the marker
                    text: "Data Date", // Marker title
                    title: dateToStr( new Date(dataDate)) // the marker's tooltip
                });
            }

            myGantt.parse(data);

            // Sort by Field
            myGantt.sort(sortType.type ? (sortType.type.toLowerCase() === 'date' ? sortDates(sortType.sortField) : compareValues(sortType.sortField)) : null);

            // Expand / Collapse All Tasks --> eachTask can only exact after Gantt.parse
            let firstTaskId = null;
            myGantt.eachTask(function (task) {
                task.$open = expandedGanttTaskIDs.indexOf(task.id) >= 0 ? true : false;
                if (firstTaskId === null) {
                    firstTaskId = task.id;
                }
            });

            myGantt.render();

            if (compConfig?.xFilter && compConfig?.xFilter?.enabled && firstTaskId !== null) {
                setTimeout(function () {
                    // console.log('selecting: ' + firstTaskId);
                    myGantt.selectTask(firstTaskId);
                }, 0);
            }
        }
    }

    useEffect( () => {

        // If Baseline Visibility or Presence changes, update displaying of them
        if (myGantt) {
            displayBaselines(baselineVisible, baselineEnabled, myGantt);
        }

    }, [baselineVisible, baselineEnabled])

    useEffect( () => {

        if (myGantt) {
            if (progressVisible) {
                // Show / Hide Progress (Percent Complete)
                myGantt.config.show_progress = true;
                myGantt.render();
            } else {
                // Show / Hide Progress (Percent Complete)
                myGantt.config.show_progress = false;
                myGantt.render();
            }
        }

    }, [progressVisible])

    const displayBaselines = (visible, enabled, myGantt) => {
        const doesElementExist = document.getElementById('dxhtmlBaselines');

        if (enabled && visible) {
            myGantt.config.bar_height = 16;
            myGantt.config.row_height = 40;

            if (!doesElementExist) {
                const css = `
                .gantt_task_line, .gantt_line_wrapper {
                    margin-top: -9px;
                }
                .gantt_side_content {
                    margin-bottom: 7px;
                }
                .gantt_task_link .gantt_link_arrow {
                    margin-top: -12px
                }
                .gantt_side_content.gantt_right {
                    bottom: 0;
                }
                .baseline {
                    position: absolute;
                    border-radius: 2px;
                    opacity: 0.6;
                    margin-top: -7px;
                    height: 6px;
                    background: #C0C0C0;
                    border: 1px solid #808080;
                }
                .baselineMilestone {
                    position: absolute;
                    border-radius: 2px;
                    opacity: 0.6;
                    margin-top: -7px;
                    height: 6px;
                    background: #C0C0C0;
                    width: 10px !important;
                    border: 1px solid #808080;
                }
                .gantt_task_line.gantt_milestone .gantt_task_content {
                    height: 12px;
                    width: 12px;
                    margin-top: -6px;
                }
                `,
                    head = document.head || document.getElementsByTagName('head')[0],
                    style = document.createElement('style');
                style.setAttribute("id", "dxhtmlBaselines");
                style.type = 'text/css';
                head.appendChild(style);
                let baselineCSS = document.createTextNode(css);
                style.appendChild(baselineCSS);
            }
        } else if (!visible) {
            // Displaying baselines logic
            myGantt.config.bar_height = 30;
            myGantt.config.row_height = 40;

            const existingStyle = document.getElementById('dxhtmlBaselines');
            if (existingStyle) existingStyle.remove();
        }

        myGantt.refreshData();
    }

    // const checkNodeInserted =(e) => {
    //     let textNode = e.target.id;
    //     if (textNode === 'dxhtmlBaselines') {
    //         setBaselineVisible(baselineVisible);
    //     }
    // }

    // const checkNodeRemoved =(e) => {
    //     let textNode = e.target.id;
    //     if (textNode === 'dxhtmlBaselines') {
    //         setBaselineVisible(!baselineVisible);
    //     }
    // }

    const handleZoomIn = (e) => {
        myGantt.ext.zoom.zoomIn();

        // // NOTE: If we want to restore zoom from modal then
        // // we need to persist and restore the current index
        // console.log(Gantt.ext.zoom);
        // console.log(Gantt.ext.zoom._activeLevelIndex);
    }

    const handleZoomOut = (e) => {
        myGantt.ext.zoom.zoomOut();
    }

    const handleDisplayBaselines = (e) => {
        setBaselineVisible(!baselineVisible);
        dispatch(appSetGanttUserSelections(id, {baselineVisible: !baselineVisible}));
    }

    const handleDisplayMilestones = (e) => {
        setMilestonesVisible(!milestonesVisible);
        setShowMilestoneKey(!showMilestoneKey);
        dispatch(appSetGanttUserSelections(id, {milestonesVisible: !milestonesVisible}));
    }

    const handleDisplaySplitParents = (e) => {
        setSplitParentsVisible(!splitParentsVisible);
        dispatch(appSetGanttUserSelections(id, {splitParentsVisible: !splitParentsVisible}));
    }

    const handleDisplayProgress = (e) => {
        setProgressVisible(!progressVisible);
        dispatch(appSetGanttUserSelections(id, {progressVisible: !progressVisible}));
    }

    const handleZoomToToday = () => {
        myGantt.scrollTo(myGantt.posFromDate(new Date()));
    }

    const handleToggleSchedule = () => {
        setTasksVisible(!tasksVisible);
        dispatch(appSetGanttUserSelections(id, {tasksVisible: !tasksVisible}));
    }

    const handleExpandAllTasks = () => {
        expandAllTasks(true);
        dispatch(appSetGanttUserSelections(id, {expandAllTasks: true}));
    }

    const handleCollapseAllTasks = () => {
        expandAllTasks(false);
        dispatch(appSetGanttUserSelections(id, {expandAllTasks: false}));
    }

    const rollupHierarchy = (tasks) => {
        const epsTasks = tasks.filter(t => { return t.nodetype === 'EPS' && t.rolledUp !== true});
        if (epsTasks.length === 0) {
            // NOTE: May not be required bc of unscheduled tasks flag however
            // assumes that if there is a baseline that there is a start/finish
            // Null out remaining outliers
            // const invalidTasks = tasks.filter(t => {
            //     return t.nodetype ===
            //         (!t.start_date || t.start_date.toString() === '1753-01-01')
            //         || (!t.planned_start_date || t.planned_start_date.toString() === '1753-01-01')
            //         || (!t.end_date || t.end_date.toString() === '9999-12-31')
            //         || (!t.planned_end_date || t.planned_end_date.toString() === '9999-12-31')
            // });
            //
            // for (const invalidTask of invalidTasks) {
            //     invalidTask.start_date = null;
            //     invalidTask.end_date = null;
            //     invalidTask.planned_start_date = null;
            //     invalidTask.planned_end_date = null;
            // }

            return;
        }

        // Roll tasks bottom i.e. lowest depth to top
        epsTasks.sort((a, b) => { return (a.depth > b.depth) ? 1 : -1})

        for (const epsTask of epsTasks) {
            let children = tasks.filter(t => { return t.parentwbsid === epsTask.id && t.rolledUp === true});

            if (children.length > 0) {

                // Rollup Schedule
                // let start_date = null;
                // let end_date = null;
                // let planned_start_date = null;
                // let planned_end_date = null;

                // Rollup Costs
                let periodplannedvaluecost_c = null;
                let periodearnedvaluecost_c = null;
                let periodactualtotalcost_c = null;
                let bac = null;
                let eac = null;
                let vac = null;
                let schedulevariance = null;
                let costvariance = null;

                for (const childTask of children) {
                    // Schedule
                    // if (childTask.start_date && childTask.start_date.toString() !== '1753-01-01') {
                    //     if (start_date === null) {
                    //         start_date = childTask.start_date;
                    //     }
                    //     else if (childTask.start_date < start_date) {
                    //         start_date = childTask.start_date;
                    //     }
                    // } else {
                    //     childTask.start_date = null;
                    // }
                    // if (childTask.end_date && childTask.end_date.toString() !== '9999-12-31') {
                    //     if (end_date === null) {
                    //         end_date = childTask.end_date;
                    //     }
                    //     else if (childTask.end_date > end_date) {
                    //         end_date = childTask.end_date;
                    //     }
                    // } else {
                    //     childTask.end_date = null;
                    // }
                    // if (childTask.planned_start_date && childTask.planned_start_date.toString() !== '1753-01-01') {
                    //     if (planned_start_date === null) {
                    //         planned_start_date = childTask.planned_start_date;
                    //     }
                    //     else if (childTask.planned_start_date < planned_start_date) {
                    //         planned_start_date = childTask.planned_start_date;
                    //     }
                    // } else {
                    //     childTask.planned_start_date = null;
                    // }
                    // if (childTask.planned_end_date && childTask.planned_end_date.toString() !== '9999-12-31') {
                    //     if (planned_end_date === null) {
                    //         planned_end_date = childTask.planned_end_date;
                    //     }
                    //     else if (childTask.planned_end_date > planned_end_date) {
                    //         planned_end_date = childTask.planned_end_date;
                    //     }
                    // } else {
                    //     childTask.planned_end_date = null;
                    // }


                    // Costs
                    if (childTask.periodplannedvaluecost_c) {
                        periodplannedvaluecost_c = (periodplannedvaluecost_c ? periodplannedvaluecost_c : 0) + childTask.periodplannedvaluecost_c;
                    }
                    if (childTask.periodearnedvaluecost_c) {
                        periodearnedvaluecost_c = (periodearnedvaluecost_c ? periodearnedvaluecost_c : 0) + childTask.periodearnedvaluecost_c;
                    }
                    if (childTask.periodactualtotalcost_c) {
                        periodactualtotalcost_c = (periodactualtotalcost_c ? periodactualtotalcost_c : 0) + childTask.periodactualtotalcost_c;
                    }
                    if (childTask.costvariance) {
                        costvariance = (costvariance ? costvariance : 0) + childTask.costvariance;
                    }
                    if (childTask.schedulevariance) {
                        schedulevariance = (schedulevariance ? schedulevariance : 0) + childTask.schedulevariance;
                    }
                    if (childTask.schedulevariance) {
                        schedulevariance = (schedulevariance ? schedulevariance : 0) + childTask.schedulevariance;
                    }
                    if (childTask.bac) {
                        bac = (bac ? bac : 0) + childTask.bac;
                    }
                    if (childTask.eac) {
                        eac = (eac ? eac : 0) + childTask.eac;
                    }
                    if (childTask.vac) {
                        vac = (vac ? vac : 0) + childTask.vac;
                    }
                }

                // Schedule
                // epsTask.start_date = start_date;
                // epsTask.end_date = end_date
                // epsTask.planned_start_date = planned_start_date;
                // epsTask.planned_end_date = planned_end_date;

                // Cost
                epsTask.periodplannedvaluecost_c = periodplannedvaluecost_c;
                epsTask.periodearnedvaluecost_c = periodearnedvaluecost_c;
                epsTask.periodactualtotalcost_c = periodactualtotalcost_c;
                epsTask.bac = bac;
                epsTask.eac = eac;
                epsTask.vac = vac;
                epsTask.schedulevariance = schedulevariance;
                epsTask.costvariance = costvariance;

                if (periodactualtotalcost_c && periodactualtotalcost_c !== 0 && periodearnedvaluecost_c) {
                    epsTask.cpi_c = periodearnedvaluecost_c / periodactualtotalcost_c;
                } else {
                    epsTask.cpi_c = null;
                }
                if (periodplannedvaluecost_c && periodplannedvaluecost_c !== 0 && periodearnedvaluecost_c) {
                    epsTask.spi_c = periodearnedvaluecost_c / periodplannedvaluecost_c;
                } else {
                    epsTask.spi_c = null;
                }
                epsTask.rolledUp = true;
            }
            else {
                // Before flipping the switch ensure we're not waiting for
                // child notes to be rolled out due to invalid depth property
                if (tasks.filter(t => { return t.parentwbsid === epsTask.id}).length === 0)
                    epsTask.rolledUp = true;
            }
        }

        rollupHierarchy(tasks);
    }

    const expandAllTasks = (expand) => {
        myGantt.eachTask(function (task) {
            task.$open = expand ? true : false;
        });
        myGantt.render();
    }

    //Setting available scales
    const scaleConfigs = [
        // decades
        {
            scales: [
                {
                    subscale_unit: "year", unit: "year", step: 10, template: function (date) {
                        let dateToStr = myGantt.date.date_to_str("%Y");
                        let endDate = myGantt.date.add(myGantt.date.add(date, 10, "year"), -1, "day");
                        return dateToStr(date) + " - " + dateToStr(endDate);
                    }
                },
                {
                    unit: "year", step: 100, template: function (date) {
                        let dateToStr = myGantt.date.date_to_str("%Y");
                        let endDate = myGantt.date.add(myGantt.date.add(date, 100, "year"), -1, "day");
                        return dateToStr(date) + " - " + dateToStr(endDate);
                    }
                }
            ]
        },
        // years
        {
            scales: [
                {subscale_unit: "year", unit: "year", step: 1, date: "%Y"},
                {
                    unit: "year", step: 5, template: function (date) {
                        let dateToStr = myGantt.date.date_to_str("%Y");
                        let endDate = myGantt.date.add(myGantt.date.add(date, 5, "year"), -1, "day");
                        return dateToStr(date) + " - " + dateToStr(endDate);
                    }
                }
            ]
        },
        // quarters
        {
            scales: [
                {subscale_unit: "month", unit: "year", step: 3, format: "%Y"},
                {
                    unit: "month", step: 3, template: function (date) {
                        let dateToStr = myGantt.date.date_to_str("%M");
                        let endDate = myGantt.date.add(myGantt.date.add(date, 3, "month"), -1, "day");
                        return dateToStr(date) + " - " + dateToStr(endDate);
                    }
                }
            ]
        },
        // months
        {
            scales: [
                {subscale_unit: "month", unit: "year", step: 1, format: "%Y"},
                {unit: "month", step: 1, format: "%M"}
            ]
        },
        // weeks
        {
            scales: [
                {subscale_unit: "week", unit: "month", step: 1, date: "%F"},
                {
                    unit: "week", step: 1, template: function (date) {
                        let dateToStr = myGantt.date.date_to_str("%d %M");
                        let endDate = myGantt.date.add(myGantt.date.add(date, 1, "week"), -1, "day");
                        return dateToStr(date) + " - " + dateToStr(endDate);
                    }
                }
            ]
        },
        // days
        {
            scales: [
                {subscale_unit: "day", unit: "month", step: 1, format: "%F"},
                {unit: "day", step: 1, format: "%j"}
            ]
        },
        // hours
        {
            scales: [
                {subscale_unit: "hour", unit: "day", step: 1, format: "%j %M"},
                {unit: "hour", step: 1, format: "%H:%i"}

            ]
        },
        // minutes
        {
            scales: [
                {subscale_unit: "minute", unit: "hour", step: 1, format: "%H"},
                {unit: "minute", step: 1, format: "%H:%i"}
            ]
        }
    ];

    // get number of columns in timeline
    function getUnitsBetween(from, to, unit, step) {
        let start = new Date(from),
            end = new Date(to);
        let units = 0;
        while (start.valueOf() < end.valueOf()) {
            units++;
            start = myGantt.date.add(start, step, unit);
        }
        return units;
    }

    function applyConfig(config, dates) {

        myGantt.config.scales = config.scales;

        if (dates && dates.start_date && dates.end_date) {
            myGantt.config.start_date = myGantt.date.add(dates.start_date, -1, config.scales[0].subscale_unit);
            myGantt.config.end_date = myGantt.date.add(myGantt.date[config.scales[0].subscale_unit + "_start"](dates.end_date), 2, config.scales[0].subscale_unit);
        } else {
            myGantt.config.start_date = myGantt.config.end_date = null;
        }
    }

    const saveConfigAndFit = (e) => {

        let config = myGantt.config;

        let settings = {};
        settings.scales = config.scales;
        settings.start_date = config.start_date;
        settings.end_date = config.end_date;

        setCachedSettings(settings);

        let project = myGantt.getSubtaskDates(),
            areaWidth = myGantt.$task.offsetWidth;

        let i;
        for (i = 0; i < scaleConfigs.length; i++) {
            let columnCount = getUnitsBetween(project.start_date, project.end_date, scaleConfigs[i].scales[0].subscale_unit, scaleConfigs[i].scales[0].step);
            if ((columnCount + 2) * myGantt.config.min_column_width >= areaWidth) {
                --i;
                break;
            }
        }


        if (i == scaleConfigs.length) {
            i--;
        }

        applyConfig(scaleConfigs[i], project);
        myGantt.render();

        setZoomedToFit(true);
    }

    const restoreConfig = () => {
        applyConfig(cachedSettings);
        myGantt.render();

        setZoomedToFit(false);
    };

    const setUserSelections = () => {

        // Baselines visible - default true
        if (baselineEnabled && toolbar?.commands?.displayBaseline?.checked === false) {
            setBaselineVisible(false);
        } else {
            const userSelection = getUserSelections("baselineVisible");
            // console.log(userSelection);
            setBaselineVisible(userSelection !== undefined ? userSelection : true);
        }

        // Progress / % Complete
        if (toolbar?.commands?.displayPercentComplete?.checked === false) {
            setProgressVisible(false);
        } else {
            const userSelection = getUserSelections("progressVisible");
            // console.log(userSelection);
            setProgressVisible(userSelection !== undefined ? userSelection : true);
        }

        // Milestones
        if (toolbar?.commands?.displayMilestone?.checked === false) {
            setMilestonesVisible(false);
        } else {
            const userSelection = getUserSelections("milestonesVisible");
            // console.log(userSelection);
            setMilestonesVisible(userSelection !== undefined ? userSelection : true);
        }

        // Split Parents
        if ((toolbar?.commands?.displaySplitParents === undefined) || (toolbar?.commands?.displaySplitParents?.checked === false)) {
            setSplitParentsVisible(false);
        } else {
            const userSelection = getUserSelections("splitParentsVisible");
            setSplitParentsVisible(userSelection !== undefined ? userSelection : true);
        }

        // Expand or Collapse Task Nodes
        const taskUserSelection = getUserSelections("expandAllTasks");
        if (taskUserSelection !== undefined) {
            expandAllTasks(taskUserSelection !== undefined ? taskUserSelection : false);
        } else {
            if (expandNodes === true) {
                expandAllTasks(true);
            } else {
                expandAllTasks(false);
            }
        }

        // Set Tasks Visible
        const taskVisibleUserSelection = getUserSelections("tasksVisible");
        if (taskVisibleUserSelection !== undefined) {
            setTasksVisible(taskVisibleUserSelection !== undefined ? taskVisibleUserSelection : true);
        } else {
            if (toolbar?.commands?.displayTask?.checked === false) {
                setTasksVisible(false);
            }
        }

    }

    const getUserSelections = (selection) => {
        // console.log(id);
        // console.log(selection);
        // console.log(userSelections);

        // Does a reference for the Gantt or Gantt Modal exist?
        const ganttSelection = userSelections.find(f => {
            const i = f.id;
            const replacedID = i.replace("-m", "");     // only saving primary component id, -m represents the components modal equivalent
            const idFound = id.startsWith(replacedID);

            if (idFound) {
                return true
            } else {
                return false
            }
        });

        // Does the selection property already exist?  If so, return set value
        let value;
        if (ganttSelection) {
            const selections = ganttSelection.selections;
            let found = false;
            found = selections.hasOwnProperty(selection);
            if (found) {
                value = selections[selection];
            }
        }

        return value;
    }

    // Set color of Task bars
    const getTaskPerformanceStyle = (d, taskType) => {
        if (taskType !== myGantt.config.types.milestone) {
            // https://docs.dhtmlx.com/gantt/desktop__colouring_tasks.html
            if (performanceStyleType) {
                if (performanceStyleType.type.toLowerCase() === 'variance') {
                    if (d[performanceStyleType.performanceField] === 0) {
                        return ragStyle.amber // On Schedule
                    } else if (d[performanceStyleType.performanceField] > 0) {
                        return ragStyle.red // Behind Schedule
                    } else {
                        return ragStyle.green // Ahead Schedule
                    }
                } else if (performanceStyleType.type.toLowerCase() === 'start') {
                    if (d[performanceStyleType.performanceFields[0]] === d[performanceStyleType.performanceFields[1]]) {
                        return ragStyle.amber // On Schedule
                    } else if (d[performanceStyleType.performanceFields[0]] > d[performanceStyleType.performanceFields[1]]) {
                        return ragStyle.red // Behind Schedule
                    } else {
                        return ragStyle.green // Ahead Schedule
                    }
                } else {
                    return null
                }
            } else {
                return null //"#F4A460"
            }
        } else {
            // Milestones and Key Milestones
            if (d[msPerfField]) {
                if (d[msPerfField].toLowerCase() === 'complete') {
                    return ragStyle.green
                } else if (d[msPerfField].toLowerCase() === 'upcoming') {
                    return ragStyle.amber
                } else if (d[msPerfField].toLowerCase() === 'overdue') {
                    return ragStyle.red
                } else {
                    return null //"#F4A460"
                }
            } else {
                return null //"#F4A460"
            }
        }
    };

	const resizeCard = () => {

		const newCardHeaderStyle = Object.assign({}, {...styles?.cardHeader});
		const newCardContentStyle = Object.assign({}, {...styles?.cardContent
			,height: 'calc(100% - 49px)'
			,overflow: 'auto'
			,padding: '8px'}
		);

		if (title && subtitle) {
			// Default Style
		} else if (title) {
			// Title Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else if (subtitle) {
			// Subtitle Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else {
			// No title
			newCardHeaderStyle.height = '0px';
			newCardHeaderStyle.padding = '0px';
			newCardContentStyle.height = '100%';
		}
		setCardHeaderStyle(newCardHeaderStyle);
		setCardContentStyle(newCardContentStyle);
	}

    const ganttStyle = {
        width: '100%'
    };
    if (toolbar?.visible || !(toolbar)) {
        ganttStyle.height = 'calc(100% - 48px)';
    } else {
        ganttStyle.height = '100%';
    }

    return (
        <Root className={classes.root}>
            {dataSource.fetching &&
                <LinearProgress className={classes.linearProgress}/>
            }
            {!dataSource.fetching &&
                <div className={classes.linearProgress}></div>
            }

            <Card className={classes.card} style={{...styles?.card}}>
                <CardHeader action={ClearFilterBtn(id, false)} className={classes.cardHeader} style={cardHeaderStyle}
                            titleTypographyProps={{color: 'textPrimary', variant: 'subtitle2'}}
                            title={t(title)}
                            subheaderTypographyProps={{color: 'textSecondary', variant: 'body2'}}
                            subheader={t(subtitle)}></CardHeader>
                <CardContent style={cardContentStyle}>
                    {(toolbar?.visible || !(toolbar)) &&
                    <GanttToolbar expandMore={{clickHandler: handleExpandAllTasks}}
                                  expandLess={{clickHandler: handleCollapseAllTasks}}
                                  zoomIn={{
                                      hidden: toolbar?.commands?.zoomIn?.hidden,
                                      clickHandler: handleZoomIn,
                                      disabled: toolbar?.commands?.zoomIn?.disabled
                                  }}
                                  zoomOut={{
                                      hidden: toolbar?.commands?.zoomOut?.hidden,
                                      clickHandler: handleZoomOut,
                                      disabled: toolbar?.commands?.zoomOut?.disabled
                                  }}
                                  zoomToday={{
                                      hidden: toolbar?.commands?.zoomToday?.hidden,
                                      clickHandler: handleZoomToToday,
                                      disabled: zoomTodayDisabled
                                  }}
                                  zoomFit={{
                                      hidden: zoomedToFit || toolbar?.commands?.zoomFit?.hidden,
                                      clickHandler: saveConfigAndFit,
                                      disabled: toolbar?.commands?.zoomFit?.disabled
                                  }}
                                  zoomRestore={{
                                      hidden: !zoomedToFit || toolbar?.commands?.zoomRestore?.hidden,
                                      clickHandler: restoreConfig,
                                      disabled: toolbar?.commands?.zoomRestore?.disabled
                                  }}
                                  displayBaseline={{
                                      hidden: toolbar?.commands?.displayBaseline?.hidden,
                                      checked: baselineVisible,
                                      disabled: !baselineEnabled,   // Enabled requires data AND configuration
                                      clickHandler: handleDisplayBaselines
                                  }}
                                  displayMilestone={{
                                      hidden: toolbar?.commands?.displayMilestone?.hidden,
                                      checked: milestonesVisible,
                                      disabled: toolbar?.commands?.displayMilestone?.disabled,
                                      clickHandler: handleDisplayMilestones
                                  }}
                                  displaySplitParents={{
                                      hidden: toolbar?.commands?.displaySplitParents?.hidden,
                                      checked: splitParentsVisible,
                                      disabled: toolbar?.commands?.displaySplitParents?.disabled,
                                      clickHandler: handleDisplaySplitParents
                                  }}
                                  displayPercentComplete={{
                                      hidden: toolbar?.commands?.displayPercentComplete?.hidden,
                                      checked: progressVisible,
                                      disabled: toolbar?.commands?.displayPercentComplete?.disabled,
                                      clickHandler: handleDisplayProgress
                                  }}
                                  displayTask={{
                                      hidden: toolbar?.commands?.displayTask?.hidden,
                                      checked: tasksVisible,
                                      disabled: toolbar?.commands?.displayTask?.disabled,
                                      clickHandler: handleToggleSchedule
                                  }}
                                  performanceKey={performanceKey}
                                  milestoneKey={showMilestoneKey === true ? milestoneKey : null}
                    />
                    }
                    <div ref={ganttRef} style={ganttStyle}></div>
                </CardContent>
            </Card>
        </Root>
    );
}
